/**
 * Vue Error Handler Plugin
 * Provides global error handling and logging for the Vue application
 */

import logger from '@/utils/logger'

export default {
  install(app) {
    // Global error handler for Vue components
    app.config.errorHandler = (error, instance, info) => {
      logger.error('Vue Component Error', error, {
        componentName: instance?.$options.name || 'Unknown',
        componentInfo: info,
        props: instance?.$props,
        route: instance?.$route?.name,
        errorBoundary: true
      })

      // In development, also log to console for debugging
      if (process.env.NODE_ENV === 'development') {
        console.error('Vue Error Details:', {
          error,
          component: instance?.$options.name,
          info,
          instance
        })
      }
    }

    // Global warning handler for Vue
    app.config.warnHandler = (msg, instance, trace) => {
      logger.warn('Vue Warning', {
        message: msg,
        componentName: instance?.$options.name || 'Unknown',
        trace,
        route: instance?.$route?.name
      })

      if (process.env.NODE_ENV === 'development') {
        console.warn('Vue Warning:', msg, trace)
      }
    }

    // Add error logging methods to global properties
    app.config.globalProperties.$logError = (error, context = {}) => {
      logger.error('Component Error', error, {
        component: 'manual_log',
        ...context
      })
    }

    app.config.globalProperties.$logWarning = (message, context = {}) => {
      logger.warn(`Component Warning: ${message}`, {
        component: 'manual_log',
        ...context
      })
    }

    app.config.globalProperties.$logInfo = (message, context = {}) => {
      logger.info(`Component Info: ${message}`, {
        component: 'manual_log',
        ...context
      })
    }

    app.config.globalProperties.$logUserAction = (action, details = {}) => {
      logger.userAction(action, details)
    }

    // Performance monitoring
    app.config.globalProperties.$logPerformance = (metric, value, context = {}) => {
      logger.performance(metric, value, context)
    }

    // Component lifecycle logging (opt-in via mixin)
    app.mixin({
      beforeCreate() {
        if (this.$options.enableLogging) {
          logger.debug('Component Created', {
            component: this.$options.name || 'Unknown',
            route: this.$route?.name
          })
        }
      },
      
      beforeUnmount() {
        if (this.$options.enableLogging) {
          logger.debug('Component Unmounting', {
            component: this.$options.name || 'Unknown',
            route: this.$route?.name
          })
        }
      },

      created() {
        // Track component render performance
        if (this.$options.trackPerformance) {
          this._renderStart = Date.now()
        }
      },

      mounted() {
        if (this.$options.trackPerformance && this._renderStart) {
          const renderTime = Date.now() - this._renderStart
          logger.performance('component_render_time', renderTime, {
            component: this.$options.name || 'Unknown',
            route: this.$route?.name
          })

          if (renderTime > 1000) {
            logger.warn('Slow Component Render', {
              component: this.$options.name || 'Unknown',
              renderTime,
              route: this.$route?.name
            })
          }
        }
      }
    })

    logger.info('Error handler plugin installed')
  }
}